import {t} from "@lingui/core/macro";
import {isNumber, map, uniq} from "lodash";

import {now, parseDateTimestamp} from "@atg-shared/datetime";
import {Flags, decide} from "@atg-shared/feature-experimentation";
import {GameType} from "@atg-tillsammans-shared/types";

import {formatGameType} from "./textFormatter";

import type {Theme} from "@atg-design-system/theme";
import type {Track as TrackDTO} from "@atg-tillsammans-shared/graphql-types";
import type {Track} from "@atg-tillsammans-shared/types";

export const toGameType = (gameType: string, defaultValue = GameType.V85): GameType =>
    Object.values(GameType).find(
        (type) => type.toUpperCase() === gameType.toUpperCase(),
    ) ?? defaultValue;

export const parseGameType = (
    gameId: string | null | undefined,
    defaultValue = GameType.V85,
): GameType => (gameId ? toGameType(gameId.split("_")[0]) : defaultValue);

export const getGameTypeColor = (
    theme: Theme,
    gameType: GameType,
): {
    default: string;
    subtle: string;
    strong: string;
} => {
    const keys = Object.keys(theme.color.fill.gametype);
    // SP96 is not in the theme; reuse BIG9 colors
    const gameTypeKey =
        gameType === GameType.SP96 ? GameType.BIG9.toLowerCase() : gameType.toLowerCase();
    if (keys.includes(gameTypeKey)) {
        const color: {default: string; subtle?: string; strong?: string} =
            theme.color.fill.gametype[
                gameTypeKey as keyof Theme["color"]["fill"]["gametype"]
            ];
        return {
            ...color,
            subtle: color?.subtle ?? color.default,
            strong: color?.strong ?? color.default,
        };
    }
    return theme.color.fill.gametype.assorted;
};

export const getGameTypeDefaultColor = (theme: Theme, gameType: GameType): string =>
    getGameTypeColor(theme, gameType).default;

export const getGradientGameTypeColor = (
    gameType: GameType | undefined,
    theme: Theme,
) => {
    const gradientOptions = {deg: 360, from: 0, to: 100};
    switch (gameType) {
        case GameType.V64:
            return theme.color.fill.gradient.v64(gradientOptions);
        case GameType.V65:
            return theme.color.fill.gradient.v65(gradientOptions);
        case GameType.V86:
            return theme.color.fill.gradient.v86(gradientOptions);
        case GameType.V85:
            return theme.color.fill.gradient.v75(gradientOptions);
        case GameType.GS75:
            return theme.color.fill.gradient.gs75(gradientOptions);
        case GameType.V75:
            return theme.color.fill.gradient.v75(gradientOptions);
        case GameType.BIG9:
        case GameType.SP96:
            return theme.color.fill.gradient.big9(gradientOptions);
        default:
            return theme.color.fill.gradient.assorted(gradientOptions);
    }
};

export const getGameTypeLabel = (gameType: GameType): string => {
    switch (gameType) {
        case GameType.BIG9:
        case GameType.SP96:
            return t`Big 9`;
        default:
            return gameType;
    }
};

export const getFormatedDate = (startTime: string) => {
    const startDate = parseDateTimestamp(startTime);
    const displayDate = startDate.isBefore(now(), "day")
        ? startDate.format("dddd D MMM")
        : startDate.format("dddd LT");
    return displayDate.charAt(0).toUpperCase() + displayDate.slice(1);
};

export const formatDate = (startTime: string, format = "dddd D MMMM") => {
    const displayDate = parseDateTimestamp(startTime).format(format);
    return displayDate.charAt(0).toUpperCase() + displayDate.slice(1);
};

export const getFormatedTracks = (tracks: Array<Track | TrackDTO>) => {
    const trackNames = uniq(map(tracks, "name")).join(", ");
    return trackNames.replace(/, ([^, ]*)$/, " & $1");
};

export const getWeekDay = (gameId: string | undefined): string => {
    if (!gameId) return "";
    const gameIdGroups = gameId.split("_");
    return parseDateTimestamp(gameIdGroups[1]).format("dddd");
};

export const getGameTypeAndWeekDay = (gameId: string | undefined): string => {
    if (!gameId) return "";
    const gameIdGroups = gameId.split("_");
    return `${formatGameType(gameIdGroups[0] as GameType)} ${formatDate(
        gameIdGroups[1],
        "dddd",
    ).toLowerCase()}`;
};

export const hasJackpot = ({
    jackpotAmount,
    estimatedJackpot,
}: {
    jackpotAmount?: number;
    estimatedJackpot?: number;
}): boolean =>
    (isNumber(jackpotAmount) && jackpotAmount > 0) ||
    (isNumber(estimatedJackpot) && estimatedJackpot > 0);

export const hasVxData = (coupon: unknown): boolean =>
    typeof coupon === "object" &&
    coupon !== null &&
    coupon !== undefined &&
    "onlyVx" in coupon &&
    typeof coupon.onlyVx === "boolean";

export const isV7Enabled = (): boolean =>
    decide(Flags.TILLSAMMANS_V7_ENABLED).variationKey === "on";

export const isOnlyVxAllowed = ({
    gameType,
    jackpotAmount,
    estimatedJackpot,
}: {
    gameType: GameType;
    jackpotAmount?: number;
    estimatedJackpot?: number;
}): boolean =>
    isV7Enabled() &&
    gameType === GameType.V75 &&
    !hasJackpot({
        jackpotAmount,
        estimatedJackpot,
    });

export function parseGameId(
    gameId: string | null | undefined,
    options: {formatDate?: (date: string) => string} = {
        formatDate: (date: string) => parseDateTimestamp(date).format("dddd"),
    },
): {gameType: GameType; date: string; trackId: number; raceNumber: number} | null {
    if (!gameId) return null;

    const [gameType, date, trackId, raceNumber] = gameId.split("_");

    return {
        gameType: toGameType(gameType),
        date: options?.formatDate ? options.formatDate(date) : date,
        trackId: parseInt(trackId, 10),
        raceNumber: parseInt(raceNumber, 10),
    };
}

export const isNewBig9 = () => decide(Flags.NEW_BIG_9).variationKey === "on";

export const isHorseGamingGreatAgain = (): boolean =>
    decide(Flags.TILLSAMMANS_MAKE_HORSE_GAMING_GREAT_AGAIN).variationKey === "on";

export const getGameTypeConfig = (): Partial<Record<GameType, boolean>> => {
    const newBig9 = isNewBig9();

    return {
        [GameType.V75]: isHorseGamingGreatAgain(),
        [GameType.V85]: !isHorseGamingGreatAgain(),
        [GameType.V86]: true,
        [GameType.SP96]: newBig9,
        [GameType.BIG9]: !newBig9,
    };
};

export const isSupportedGameType = (gameType: GameType): boolean =>
    getGameTypeConfig()[gameType] ?? false;

export function getSelectableGameTypes(): GameType[] {
    return Object.entries(getGameTypeConfig()).reduce<GameType[]>(
        (acc, [gameType, isEnabled]) => {
            if (isEnabled) {
                acc.push(toGameType(gameType));
            }
            return acc;
        },
        [],
    );
}

export const getAllowedHorseGameTypes = (): GameType[] =>
    Object.entries(getGameTypeConfig()).reduce<GameType[]>(
        (acc, [gameType, isEnabled]) => {
            if (gameType === GameType.BIG9 || gameType === GameType.SP96) return acc; // BIG9 and SP96 are not a horse game types

            if (isEnabled) {
                acc.push(toGameType(gameType));
            }
            return acc;
        },
        [],
    );
